import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import FormField from ".";
import DisplayField from "./DisplayField";
import { InputAdornment } from "@mui/material";
import EventService from "@shared/services/EventService";
import { I18n } from "../LangUtil";
import { SELECT_FIELD_HIGHLIGHT_DEFAULT_DURATION } from "./constants";

const SelectField = React.forwardRef((props, ref) => {
    const {
        label,
        value,
        defaultValue,
        options = [],
        optionGenerator,
        dropdownIndicator,
        dropdownContainerClass,
        InputProps,
        onChangeInput,
        selectFirst = true,
        onFocusHighlightDuration = SELECT_FIELD_HIGHLIGHT_DEFAULT_DURATION,
        ...restProps
    } = props;

    const containerRef = useRef(null);

    const [highlighted, setHighlighted] = useState(false);
    const removeHighlightTimeoutRef = useRef(null);

    useEffect(() => {
        /**
         * scenario 1 - if value is passed instead of object and is present in the given options
         */
        if (value !== "" && typeof value !== "object") {
            let val = options.find(opt => opt.value === value);
            if (val) {
                onChangeInput(val);
            }
        }

        /**
         * scenario 2 - if defaultValue is passed
         */
        if (value === "" && defaultValue && defaultValue !== "") {
            if (typeof defaultValue === "object") {
                let val = options.find(opt => opt.value == defaultValue.value);
                if (val) {
                    onChangeInput(val);
                }
            } else {
                let val = options.find(opt => opt.value == defaultValue);
                if (val) {
                    onChangeInput(val);
                }
            }
        }

        /**
         * scenario 3 - populate single option
         */
        if (!value && !defaultValue && options.length === 1 && selectFirst) {
            onChangeInput(options[0]);
        }

        /**
         * scenario 4 - clearing select if current value not in new options
         */
        if (
            value &&
            typeof value === "object" &&
            value.value &&
            !options.find(opt => (value || {}).value == opt.value)
        ) {
            onChangeInput("");
        }

        return () => {};
    }, [value, defaultValue, options]);

    const onOpenDropdown = () => {
        EventService.publish("globalModal.show", {
            component: SelectDropdown,
            componentProps: {
                label,
                options,
                optionGenerator,
                dropdownContainerClass,
                onSelectOption: newVal => {
                    onChangeInput(newVal);
                    EventService.publish("globalModal.hide");
                },
                onCancel: () => {
                    EventService.publish("globalModal.hide");
                },
                ...restProps
            },
            modalProps: {
                hideClose: true,
                closeOnClickAway: false
            }
        });
    };

    const handleFocus = () => {
        setHighlighted(true);

        // Remove highlight after ${onFocusHighlightDuration}s
        removeHighlightTimeoutRef.current = setTimeout(() => {
            setHighlighted(false);
        }, onFocusHighlightDuration);
    };

    const handleBlur = () => {
        clearTimeout(removeHighlightTimeoutRef.current);
        setHighlighted(false);
    };

    useImperativeHandle(ref, () => ({
        //different from traditional "focus", it highlights and scrolls the component
        focus: () => {
            containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            handleFocus();
        }
    }));

    return (
        <DisplayField
            {...restProps}
            ref={containerRef}
            type="text"
            label={label}
            value={value ? value.label : ""}
            {...(props?.disabled === true
                ? {}
                : {
                    onClick: () => {
                        if (props.onOpenDropdown) return props.onOpenDropdown();
                        onOpenDropdown();
                    }
                })}
            onFocus={handleFocus}
            onBlur={handleBlur}
            containerClass={`${highlighted ? "highlighted" : ""}`}
            InputProps={props?.disabled===true ?{}:{
                endAdornment: (
                    <InputAdornment position="end">
                        {dropdownIndicator || (
                            <img
                                className="search-adornment"
                                src={require(`@app/styles/img/right-arrow.svg`)}
                                alt="logo"
                            />
                        )}
                    </InputAdornment>
                ),
                ...InputProps
            }}
        />
    );
});

export default SelectField;

export const SelectDropdown = ({
    label,
    options = [],
    optionGenerator,
    dropdownContainerClass,
    onSelectOption,
    onCancel,
    ...restProps
}) => {
    const [searchText, setSearchText] = useState("");
    const [selectedOption, setSelectedOption] = useState("");

    const searchFieldRef = useRef();

    const { virtualKeyboardLang = null, virtualKeyboardLayout = null } = restProps;

    useEffect(() => {
        searchFieldRef.current?.focus();
    }, []);

    const _options = options
    .filter(o => {
        const label = (o.label || "").toLowerCase();
        const keywords = (o.searchKeywords || []).map(keyword => keyword.toLowerCase());
        const searchTextLower = searchText.toLowerCase();
        return label.includes(searchTextLower) || keywords.some(keyword => keyword.startsWith(searchTextLower));
    })
    .sort((a, b) => {
        if (a.priorityList && !b.priorityList) {
            return -1;
        } else if (!a.priorityList && b.priorityList) {
            return 1; 
        } else if (a.priorityList && b.priorityList) {
            return 0;
        } else {
            const labelA = (a.label || "").toLowerCase();
            const labelB = (b.label || "").toLowerCase();
            const searchKeywordsA = (a.searchKeywords || []).map(keyword => keyword.toLowerCase());
            const searchKeywordsB = (b.searchKeywords || []).map(keyword => keyword.toLowerCase());
            const startsWithA = labelA.startsWith(searchText.toLowerCase());
            const startsWithB = labelB.startsWith(searchText.toLowerCase());
            const matchesA = searchKeywordsA.includes(searchText.toLowerCase());
            const matchesB = searchKeywordsB.includes(searchText.toLowerCase());

            if (matchesA && !matchesB) {
                return -1;
            } else if (!matchesA && matchesB) {
                return 1;
            } else if (startsWithA && !startsWithB) {
                return -1;
            } else if (!startsWithA && startsWithB) {
                return 1;
            } else {
                return labelA.localeCompare(labelB);
            }
        }
    });






    return (
        <div className={`card-option ${dropdownContainerClass || ""} `}>
            {options.length && options.length <= 10 ? (
                <>
                    <div className="Choose-Heading" style={{ margin: "53px" }}>
                        Select {label || "Option"}
                    </div>
                    <div className="grid-select-container">
                        {options.map((opt, i) => {
                            return (
                                <button
                                    key={i}
                                    className={`purpose-box ${selectedOption?.value === opt.value ? "selected" : ""}`}
                                    // onClick={() => setSelectedOption(opt)}
                                    onClick={() => onSelectOption(opt)}
                                >
                                    {optionGenerator ? optionGenerator(opt) : opt.label}
                                </button>
                            );
                        })}
                    </div>
                </>
            ) : (
                <>
                    <div className="option-searchBar">
                        <FormField
                            ref={searchFieldRef}
                            controlled={true}
                            type={"text"}
                            placeholder={I18n("g.lbl.typeheretosearchopt")}
                            value={searchText}
                            onChange={newVal => setSearchText(newVal)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <img
                                            src={require(`@app/styles/img/searchicon.svg`)}
                                            alt="search"
                                            className="search"
                                        />
                                    </InputAdornment>
                                )
                            }}
                            // Intentionally avoiding spreading restProps to prevent unintended props from being passed,
                            // which could lead to unpredictable behavior in the form field.
                            {...(virtualKeyboardLang ? { virtualKeyboardLang: virtualKeyboardLang } : {})}
                            {...(virtualKeyboardLayout ? { virtualKeyboardLayout: virtualKeyboardLayout } : {})}
                        />
                    </div>



                    <ul className="options">
                        {/* {_options.length ? (
                            _options.map((opt, i) => {
                                console.log("opt.priorityList", opt.priorityList);
                                return opt.priorityList && (
                                    <li
                                        key={i}
                                        className={selectedOption?.value === opt.value ? "selected" : ""}
                                        // onClick={() => setSelectedOption(opt)}
                                        onClick={() => onSelectOption(opt)}
                                    >
                                        {optionGenerator ? optionGenerator(opt) : opt.label}
                                    </li>
                                );
                            })
                        ) : ("")} */}
                        {_options.length ? (
                            _options.map((opt, i) => {
                                return  (
                                    <li
                                        key={i}
                                        className={selectedOption?.value === opt.value ? "selected" : ""}
                                        // onClick={() => setSelectedOption(opt)}
                                        onClick={() => onSelectOption(opt)}
                                    >
                                        {optionGenerator ? optionGenerator(opt) : opt.label}
                                    </li>
                                );
                            })
                        ) : (
                            <li>No records found</li>
                        )}
                    </ul>
                </>
            )}
            <div className="btn-div">
                <button className="primary-btn btn cancel-btn" onClick={() => onCancel()}>
                    {I18n("g.action.cancel")}
                </button>
                {/* <button
                    className={`success-btn btn select-btn ${!selectedOption ? "disabled" : ""}`}
                    onClick={() => {
                        if (selectedOption) {
                            onSelectOption(selectedOption);
                        }
                    }}
                >
                    Select
                </button> */}
            </div>
        </div>
    );
};
